import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, TextField, Button, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import '../styles/pdfExport.css';

const ParametrosTurnos = () => {
  // Cargar estado desde localStorage o usar valores por defecto
  const [numTurnos, setNumTurnos] = useState(() => {
    const saved = localStorage.getItem('parametrosTurnos_numTurnos');
    return saved ? parseInt(saved, 10) : 1;
  });
  const [turnos, setTurnos] = useState(() => {
    const saved = localStorage.getItem('parametrosTurnos_turnos');
    return saved ? JSON.parse(saved) : [{ inicio: '', fin: '' }];
  });
  const [result, setResult] = useState(() => {
    const saved = localStorage.getItem('parametrosTurnos_result');
    return saved ? JSON.parse(saved) : null;
  });
  const [fechaInicio, setFechaInicio] = useState(() => {
    return localStorage.getItem('parametrosTurnos_fechaInicio') || '';
  });
  const [fechaFin, setFechaFin] = useState(() => {
    return localStorage.getItem('parametrosTurnos_fechaFin') || '';
  });

  // Guardar en localStorage cuando cambian los estados
  useEffect(() => {
    localStorage.setItem('parametrosTurnos_numTurnos', numTurnos);
  }, [numTurnos]);

  useEffect(() => {
    localStorage.setItem('parametrosTurnos_turnos', JSON.stringify(turnos));
  }, [turnos]);

  useEffect(() => {
    if (result) {
      localStorage.setItem('parametrosTurnos_result', JSON.stringify(result));
    }
  }, [result]);

  useEffect(() => {
    localStorage.setItem('parametrosTurnos_fechaInicio', fechaInicio);
  }, [fechaInicio]);

  useEffect(() => {
    localStorage.setItem('parametrosTurnos_fechaFin', fechaFin);
  }, [fechaFin]);

  const handleNumTurnosChange = (e) => {
    const n = parseInt(e.target.value, 10);
    setNumTurnos(n);
    setTurnos(Array(n).fill().map((_, i) => turnos[i] || { inicio: '', fin: '' }));
  };

  const handleTurnoChange = (i, field, value) => {
    const nuevosTurnos = [...turnos];
    nuevosTurnos[i][field] = value;
    setTurnos(nuevosTurnos);
  };

  const validarTurnos = () => {
    for (let i = 0; i < turnos.length; i++) {
      const inicio = turnos[i].inicio;
      const fin = turnos[i].fin;

      if (!inicio || !fin) {
        alert(`Debes ingresar la hora de inicio y fin para el turno ${i + 1}`);
        return false;
      }

      // 1. La hora inicial del primer turno debe ser menor que la hora final del turno 1
      if (i === 0 && inicio >= fin) {
        alert('La hora inicial del primer turno debe ser menor que la hora final del primer turno.');
        return false;
      }

      // 2. La hora inicial del segundo turno debe ser igual o mayor a la hora final del turno 1
      if (i === 1 && inicio < turnos[0].fin) {
        alert('La hora inicial del segundo turno debe ser igual o mayor a la hora final del primer turno.');
        return false;
      }

      // 3. La hora inicial del tercer turno debe ser igual o mayor que la hora final del turno 2
      if (i === 2 && inicio < turnos[1].fin) {
        alert('La hora inicial del tercer turno debe ser igual o mayor que la hora final del segundo turno.');
        return false;
      }

      // 4. La hora final del tercer turno debe ser mayor que la hora inicial del tercer turno
      if (i === 2 && fin <= inicio) {
        alert('La hora final del tercer turno debe ser mayor que la hora inicial del tercer turno.');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validarTurnos()) return;
    try {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/acumulado-turnos-dia`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ turnos, fechaInicio, fechaFin })
      });
      const data = await response.json();
      setResult(data);
    } catch (error) {
      setResult({ error: 'Error consultando turnos' });
    }
  };

  // Función para descargar la tabla como PDF
  const descargarPDF = () => {
    const input = document.getElementById('tabla-turnos');
    if (!input) return;
    input.classList.add('pdf-export');
    
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const marginX = 10;
      const marginY = 15;
      const imgWidth = pdfWidth - (marginX * 2);
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
      const pageContentHeight = pdfHeight - (marginY * 2);
      
      let heightLeft = imgHeight;
      let position = marginY;
      
      pdf.addImage(imgData, 'PNG', marginX, position, imgWidth, imgHeight);
      heightLeft -= pageContentHeight;
      
      while (heightLeft > 0) {
        position = -(imgHeight - heightLeft) + marginY;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', marginX, position, imgWidth, imgHeight);
        heightLeft -= pageContentHeight;
      }
      
      pdf.save('segmentacion-turnos.pdf');
      input.classList.remove('pdf-export');
    });
  };

  // Función para formatear números sin decimales y con separador de miles
  const formatearNumero = (valor) => {
    const num = Number(valor) || 0;
    return Math.round(num).toLocaleString('es-ES');
  };

  // Procesa los datos para la tabla agrupando por turno y fecha
  let maquinas = [];
  let matriz = [];
  let totalesMaquina = {};
  let totalGeneral = 0;

  if (Array.isArray(result)) {
    maquinas = result.map(r => r.dispositivoId);

    // 1. Extrae todos los periodos únicos (por turno y fecha)
    let periodosUnicos = [];
    result.forEach(machine => {
      Object.entries(machine).forEach(([key, value]) => {
        if (key.startsWith('turno') && value.periodos) {
          value.periodos.forEach(periodo => {
            const id = `${key}_${periodo.fecha}`; // Solo fecha y turno
            if (!periodosUnicos.find(p => p.id === id)) {
              periodosUnicos.push({
                id,
                turno: key,
                fecha: periodo.fecha,
                maquinas: {},
                total: 0
              });
            }
          });
        }
      });
    });

    // 2. Llena los valores de cada máquina en cada periodo único (una fila por fecha y turno)
    periodosUnicos.forEach(periodo => {
      let sumaMaquinas = 0;
      maquinas.forEach(maquinaId => {
        const machine = result.find(r => r.dispositivoId === maquinaId);
        let rpm = '';
        Object.entries(machine).forEach(([key, value]) => {
          if (key === periodo.turno && value.periodos) {
            const match = value.periodos.find(p =>
              p.fecha === periodo.fecha
            );
            if (match) rpm = match.rpmTurno;
          }
        });
        periodo.maquinas[maquinaId] = rpm;
        sumaMaquinas += Number(rpm) || 0;
      });
      periodo.total = sumaMaquinas;
    });

    matriz = periodosUnicos;

    // 3. Totales por máquina (suma de todos los periodos)
    totalesMaquina = {};
    maquinas.forEach(id => {
      totalesMaquina[id] = matriz.reduce((sum, fila) => sum + (Number(fila.maquinas[id]) || 0), 0);
    });
    totalGeneral = matriz.reduce((sum, fila) => sum + fila.total, 0);
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h5" gutterBottom>
          Segmentacion por horas
        </Typography>
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 2, width: '100%' }}>
            <TextField
              label="Fecha de inicio del periodo"
              type="date"
              value={fechaInicio}
              onChange={e => setFechaInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ mb: 2 }}
              required
            />
            <TextField
              label="Fecha de fin del periodo"
              type="date"
              value={fechaFin}
              onChange={e => setFechaFin(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ mb: 2 }}
              required
            />
            <TextField
              select
              label="Número de turnos"
              value={numTurnos}
              onChange={handleNumTurnosChange}
              SelectProps={{ native: true }}
              fullWidth
              sx={{
                '& select': {
                  backgroundColor: '#333',
                }
              }}
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </TextField>
          </Box>
          <Grid container spacing={12}>
            {turnos.map((turno, i) => (
              <React.Fragment key={i}>
                <Grid item xs={6}>
                  <TextField
                    label={`Hora inicio turno ${i + 1}`}
                    type="time"
                    value={turno.inicio}
                    onChange={e => handleTurnoChange(i, 'inicio', e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={`Hora fin turno ${i + 1}`}
                    type="time"
                    value={turno.fin}
                    onChange={e => handleTurnoChange(i, 'fin', e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button type="submit" variant="contained" color="primary">
              Consultar Turnos
            </Button>
          </Box>
        </form>
        {result && Array.isArray(result) && (
          <Box sx={{ mt: 4, width: '100%' }}>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button variant="outlined" color="secondary" onClick={descargarPDF}>
                Descargar PDF
              </Button>
            </Box>
            <Box id="tabla-turnos">
              <Typography variant="h5" align="center" sx={{ mb: 2, color: '#555', fontWeight: 'bold' }}>
                Segmentación por Turnos
              </Typography>
              <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                {turnos.map((turno, i) => (
                  <Typography key={i} variant="body1" sx={{ color: '#000', fontWeight: 'bold' }}>
                    Turno {i + 1}: {turno.inicio} - {turno.fin}
                  </Typography>
                ))}
              </Box>
              <TableContainer component={Paper} sx={{ maxHeight: '600px' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#333435', color: 'white' }}>Turno / Fecha</TableCell>
                      {maquinas.map(id => (
                        <TableCell key={id} sx={{ fontWeight: 'bold', backgroundColor: '#333435', color: 'white' }}>{id}</TableCell>
                      ))}
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#333435', color: 'white' }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* eslint-disable-next-line no-unused-vars */}
                    {matriz.map((fila, idx) => (
                      <TableRow key={fila.id} sx={{ '&:hover': { backgroundColor: '#424242' } }}>
                        <TableCell>
                          {fila.turno}<br />
                          {fila.fecha}
                        </TableCell>
                        {maquinas.map(id => (
                          <TableCell key={id}>{formatearNumero(fila.maquinas[id])}</TableCell>
                        ))}
                        <TableCell sx={{ fontWeight: 'bold' }}>{formatearNumero(fila.total)}</TableCell>
                      </TableRow>
                    ))}
                    {/* Fila de totales */}
                    <TableRow sx={{ backgroundColor: '#333435' }}>
                      <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Total</TableCell>
                      {maquinas.map(id => (
                        <TableCell key={id} sx={{ fontWeight: 'bold', color: 'white' }}>{formatearNumero(totalesMaquina[id])}</TableCell>
                      ))}
                      <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>{formatearNumero(totalGeneral)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        )}
        {result && result.error && (
          <Box sx={{ mt: 4, width: '100%' }}>
            <Typography color="error">{result.error}</Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default ParametrosTurnos;